import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import aspectRatios, { AspectRatioIcon } from '../../../constants/aspectRatios';
import resolutionMap, { RESOLUTION_OPTIONS } from '../../../constants/resolutionMap';
import AngleViewDropdown from '../../../components/AngleViewDropdown';
import ModelSelectionDropdown from '../../../components/ModelSelectionDropdown';
import { isGeneratedImageDrop, getGeneratedImageUrl } from '../../../utils/dragDropUtils';
import { getModelCreditCost } from '../../../utils/creditCost';
import { isFluxModel } from '../../../services/fluxService';


const PromptTaskBar = ({ isGenerating, onGenerate, onCancel, disabled, libraryAssets = [], setLibraryAssets, initialPrompt = '', initialModel = '', initialImageSize = '', promptGuideRef, angleGuideRef, modelGuideRef, aspectRatioGuideRef, resolutionGuideRef, generateGuideRef, activeGuideStep, showGuide, guideVisible }) => {
  const [promptText, setPromptText] = useState('');
  const [selectedAngleShot, setSelectedAngleShot] = useState('--');
  const [commonAngleView, setCommonAngleView] = useState('frontal');
  const [selectedModel, setSelectedModel] = useState('Gemini 3.1 Flash Image Preview');
  const [selectedAspectRatio, setSelectedAspectRatio] = useState('Auto');
  const [isAspectRatioOpen, setIsAspectRatioOpen] = useState(false);
  const [selectedResolution, setSelectedResolution] = useState('1K');
  const [isResolutionOpen, setIsResolutionOpen] = useState(false);
  const textareaRef = useRef(null);
  const aspectRatioRef = useRef(null);
  const resolutionRef = useRef(null);

  // Restore initial values from parent (e.g. when loading from project history)
  useEffect(() => {
    if (initialPrompt) setPromptText(initialPrompt);
  }, [initialPrompt]);
  useEffect(() => {
    if (initialModel) setSelectedModel(initialModel);
  }, [initialModel]);
  useEffect(() => {
    if (initialImageSize) setSelectedAspectRatio(initialImageSize);
  }, [initialImageSize]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (aspectRatioRef.current && !aspectRatioRef.current.contains(e.target)) {
        setIsAspectRatioOpen(false);
      }
      if (resolutionRef.current && !resolutionRef.current.contains(e.target)) {
        setIsResolutionOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleGenerate = () => {
    if (onGenerate) {
      onGenerate({
        promptText,
        selectedAngleShot,
        commonAngleView,
        selectedModel,
        selectedAspectRatio,
        selectedResolution,
        libraryAssets,
      });
    }
  };

  const removeLibraryAsset = (img) => {
    setLibraryAssets((prev) => prev.filter((a) => a !== img));
  };

  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const handleTaskBarDragOver = (e) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleTaskBarDragLeave = (e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDraggingOver(false);
    }
  };

  const handleTaskBarDrop = (e) => {
    e.preventDefault();
    setIsDraggingOver(false);

    if (isGeneratedImageDrop(e)) {
      const url = getGeneratedImageUrl(e);
      if (url) {
        setLibraryAssets((prev) => [...prev, url]);
      }
      return;
    }

    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 0) {
      const urls = files.filter(f => f.type.startsWith('image/')).map((file) => URL.createObjectURL(file));
      setLibraryAssets((prev) => [...prev, ...urls]);
    }
  };

  const glowStyle = (stepNum, radius = '1rem') => ({
    position: showGuide && activeGuideStep === stepNum ? 'relative' : undefined,
    zIndex: showGuide && activeGuideStep === stepNum ? 201 : undefined,
    borderRadius: radius,
    boxShadow: showGuide && activeGuideStep === stepNum && guideVisible
      ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)'
      : '0 0 0px transparent',
    transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  });

  const getResolutionDimensions = (resKey) => {
    const ratioMap = resolutionMap[selectedAspectRatio] || resolutionMap['1:1'];
    return ratioMap ? ratioMap[resKey] || { width: 1024, height: 1024 } : { width: 1024, height: 1024 };
  };

  return (
    <div className="glassmorphic-card rounded-3xl p-6 border border-orange-500/20 shadow-[0_0_30px_rgba(249,115,22,0.15)]">
      {/* First Level: Plus Button + Textarea — Step 6 */}
      <div ref={promptGuideRef} style={glowStyle(5)}>
      <div className="flex items-start gap-3 mb-6">
        <label className="cursor-pointer flex-shrink-0">
          <input
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e?.target?.files || []);
              const urls = files.map((file) => URL.createObjectURL(file));
              setLibraryAssets((prev) => [...prev, ...urls]);
            }}
          />
          <div className="w-12 h-12 glassmorphic-button rounded-2xl flex items-center justify-center hover:scale-105 transition-transform">
            <Icon name="Plus" size={20} className="text-white" />
          </div>
        </label>

        <div
          className="flex-1 relative"
          onDragOver={handleTaskBarDragOver}
          onDragLeave={handleTaskBarDragLeave}
          onDrop={handleTaskBarDrop}
        >
          <textarea
            ref={textareaRef}
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            placeholder="Add reference images, or additional text request. Drag and drop images here."
            rows={5}
            className={`w-full bg-white/5 border rounded-2xl text-white text-xs placeholder:text-white/40 focus:outline-none focus:border-orange-500/50 px-3 py-2 resize-none overflow-y-auto transition-all min-h-[120px] max-h-[300px] ${
              isDraggingOver ? 'border-orange-500 bg-orange-500/10' : 'border-white/10'
            }`}
            style={{ height: '120px' }}
          />

          {libraryAssets.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {libraryAssets.map((img, idx) => (
                <div key={`library-${idx}`} className="relative group">
                  <img
                    src={img}
                    alt={`Library ${idx + 1}`}
                    className="w-12 h-12 object-cover rounded-lg border border-blue-500/50"
                  />
                  <button
                    onClick={() => removeLibraryAsset(img)}
                    className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Icon name="X" size={10} className="text-white" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Second Level: Common Angle Views Toggle + Angle View Dropdown — Step 7 */}
      <div ref={angleGuideRef} style={glowStyle(6)}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Common Angle Views Toggle */}
        <div>
          <label className="text-white/70 text-xs mb-3 block">Common angle views</label>
          <div className="flex items-center justify-start gap-2">
            <span className={`text-xs transition-colors ${commonAngleView === 'frontal' ? 'text-white' : 'text-white/50'}`}>
              Frontal view
            </span>
            <button
              onClick={() => {
                if (commonAngleView === 'frontal') {
                  setCommonAngleView('topdown');
                  setSelectedAngleShot('Top-Down Flat View (90°)');
                } else {
                  setCommonAngleView('frontal');
                  setSelectedAngleShot('Straight-On Eye-Level View');
                }
              }}
              className={`relative w-14 h-7 rounded-full transition-all duration-300 ${commonAngleView ? 'bg-orange-500' : 'bg-white/20'}`}
            >
              <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${commonAngleView === 'frontal' ? 'left-0.5' : 'right-0.5'}`} />
            </button>
            <span className={`text-xs transition-colors ${commonAngleView === 'topdown' ? 'text-white' : 'text-white/50'}`}>
              Top-down view
            </span>
          </div>
        </div>

        {/* Angle Shot Dropdown */}
        <div>
          <AngleViewDropdown value={selectedAngleShot} onChange={(val) => {
            setSelectedAngleShot(val);
            if (val === 'Straight-On Eye-Level View') {
              setCommonAngleView('frontal');
            } else if (val === 'Top-Down Flat View (90°)') {
              setCommonAngleView('topdown');
            } else {
              setCommonAngleView(null);
            }
          }} />
        </div>
      </div>
      </div>

      {/* Third Level: Model Selection, Aspect Ratio, Generate Button */}
      <div className="space-y-6">
        {/* Step 7: Model Selection */}
        <div className="grid grid-cols-2 gap-4">
          <div ref={modelGuideRef} style={glowStyle(7)}>
          <ModelSelectionDropdown
            value={selectedModel}
            onChange={setSelectedModel}
          />
          </div>

          {/* Step 8: Aspect Ratio */}
          <div ref={aspectRatioGuideRef} style={glowStyle(8)}>
          <div ref={aspectRatioRef}>
            <label className="text-white/70 text-xs mb-2 block">Aspect Ratio</label>
            <div className="relative">
              <button
                onClick={() => setIsAspectRatioOpen(!isAspectRatioOpen)}
                className="w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all"
              >
                <div className="flex items-center gap-3">
                  <AspectRatioIcon
                    ratio={selectedAspectRatio}
                    size={18}
                    className="text-orange-500"
                  />
                  <span className="text-white text-sm">{selectedAspectRatio}</span>
                </div>
                <Icon
                  name={isAspectRatioOpen ? 'ChevronUp' : 'ChevronDown'}
                  size={16}
                  className="text-white/50"
                />
              </button>

              {isAspectRatioOpen && (
                <div
                  className="absolute z-50 w-full bottom-full mb-2 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                  style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}
                >
                  {aspectRatios.map((ratio) => (
                    <button
                      key={ratio.value}
                      onClick={() => {
                        setSelectedAspectRatio(ratio.value);
                        setIsAspectRatioOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${
                        selectedAspectRatio === ratio.value
                          ? 'bg-orange-500/20 hover:bg-orange-500/30'
                          : 'hover:bg-white/5'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <AspectRatioIcon
                          ratio={ratio.value}
                          size={18}
                          className={selectedAspectRatio === ratio.value ? 'text-orange-500' : 'text-white/50'}
                        />
                        <span className={`text-sm ${selectedAspectRatio === ratio.value ? 'text-white font-medium' : 'text-white/70'}`}>
                          {ratio.label}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          </div>

        </div>

        {/* Step 9: Resolution Selection */}
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div ref={resolutionGuideRef} style={glowStyle(9)}>
          {!isFluxModel(selectedModel) && (
          <div ref={resolutionRef}>
            <label className="text-white/70 text-xs mb-2 block">Resolution</label>
            <div className="relative">
              <button
                onClick={() => setIsResolutionOpen(!isResolutionOpen)}
                className={`w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center gap-3 justify-between hover:border-orange-500/30 transition-all`}
              >
                <span className="text-white text-sm font-medium">{selectedResolution}</span>
                <div className="flex items-center gap-2">
                  <span className="text-white/50 text-xs">
                      {getResolutionDimensions(selectedResolution).width}×{getResolutionDimensions(selectedResolution).height}
                    </span>
                  <Icon name={isResolutionOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-white/50" />
                </div>
              </button>

              {isResolutionOpen && (
                <div
                  className="absolute z-50 w-full bottom-full mb-2 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                  style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}
                >
                  {RESOLUTION_OPTIONS.map((resKey) => {
                    const dims = getResolutionDimensions(resKey);
                    return (
                      <button
                        key={resKey}
                        onClick={() => {
                          setSelectedResolution(resKey);
                          setIsResolutionOpen(false);
                        }}
                        className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${
                          selectedResolution === resKey
                            ? 'bg-orange-500/20 hover:bg-orange-500/30'
                            : 'hover:bg-white/5'
                        }`}
                      >
                        <span className={`text-sm ${selectedResolution === resKey ? 'text-white font-medium' : 'text-white/70'}`}>
                          {resKey}
                        </span>
                        <span className={`text-xs ${selectedResolution === resKey ? 'text-orange-400' : 'text-white/50'}`}>
                            {dims.width}×{dims.height}
                          </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          )}
          </div>
        </div>

        {/* Step 10: Generate Button */}
        <div ref={generateGuideRef} style={glowStyle(10, '1rem')}>
        <button
          onClick={handleGenerate}
          disabled={disabled || isGenerating}
          className="w-full glassmorphic-button rounded-2xl py-3 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGenerating ? (
            <>
              <Icon name="Loader" size={18} className="animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-sparkles"
              >
                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                <path d="M20 3v4" />
                <path d="M22 5h-4" />
                <path d="M4 17v2" />
                <path d="M5 18H3" />
              </svg>
              Generate Image
            </>
          )}
        </button>
        </div>

        {isGenerating && onCancel && (
          <button
            onClick={onCancel}
            className="w-full glassmorphic-button rounded-2xl py-2 text-sm text-white/70 font-medium flex items-center justify-center gap-2 hover:text-white transition-colors"
          >
            <Icon name="X" size={16} />
            Cancel
          </button>
        )}

        <p className="text-white/50 text-xs text-center">
          Your request will cost {getModelCreditCost(selectedModel)} credit{getModelCreditCost(selectedModel) !== 1 ? 's' : ''}
        </p>
      </div>
    </div>
  );
};

export default PromptTaskBar;
